import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { cn } from "../../utils/cn";
import { useLocation, useNavigate } from "react-router-dom"; // Import hooks for query params

interface TabItem {
  label: string;
  value: string;
}

interface TabsProps {
  items: TabItem[];
  defaultValue?: string;
  onChange?: (value: string) => void;
  className?: string;
  tabIndicatorClassName?: string;
  tabItemClassName?: string;
  activeTabItemClassName?: string;
  inactiveTabItemClassName?: string;
}

export const FleetrifyTabs: React.FC<TabsProps> = ({
  items,
  defaultValue = items[0]?.value,
  onChange,
  className,
  tabIndicatorClassName,
  tabItemClassName,
  activeTabItemClassName,
  inactiveTabItemClassName,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(defaultValue);
  const [indicatorPosition, setIndicatorPosition] = useState({
    left: 0,
    width: 0,
  });
  const containerRef = React.useRef<HTMLDivElement>(null);
  const tabRefs = React.useRef<HTMLButtonElement[]>([]);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const queryTab = urlParams.get("tab") || defaultValue;
    setActiveTab(queryTab);
  }, [location.search, defaultValue]);

  const handleTabClick = (value: string) => {
    setActiveTab(value);
    onChange?.(value);

    const searchParams = new URLSearchParams(location.search);
    searchParams.set("tab", value);
    navigate(`${location.pathname}?${searchParams.toString()}`, {
      replace: true,
    });
  };

  useEffect(() => {
    const activeTabIndex = items.findIndex((item) => item.value === activeTab);
    if (
      activeTabIndex !== -1 &&
      containerRef.current &&
      tabRefs.current[activeTabIndex]
    ) {
      const activeTabElement = tabRefs.current[activeTabIndex];
      const containerRect = containerRef.current.getBoundingClientRect();
      const tabRect = activeTabElement.getBoundingClientRect();

      setIndicatorPosition({
        left: tabRect.left - containerRect.left,
        width: tabRect.width,
      });
    }
  }, [activeTab, items]);

  return (
    <div
      className={cn(
        "relative flex w-fit overflow-hidden items-center",
        "bg-[#F5F6FA] ",
        "rounded-lg",
        className
      )}
      ref={containerRef}
    >
      {items.map((item, index) => {
        const isActive = activeTab === item.value;
        return (
          <button
            key={item.value}
            ref={(el) => {
              if (el) tabRefs.current[index] = el;
            }}
            onClick={() => handleTabClick(item.value)}
            className={cn(
              "relative z-10 px-6 py-2 min-w-fit w-full outline-none transition-colors duration-300",
              "flex-1 text-sm font-medium",
              tabItemClassName,
              isActive
                ? cn("text-white", activeTabItemClassName)
                : cn(
                    "text-[#828282] dark:text-[#828282] hover:text-[#143887] ",
                    inactiveTabItemClassName
                  )
            )}
            aria-pressed={isActive}
          >
            {item.label}
          </button>
        );
      })}
      <motion.div
        className={cn(
          "absolute bottom-0 !h-full bg-[#143887] ",
          "transition-all duration-300",
          tabIndicatorClassName
        )}
        style={{
          left: indicatorPosition.left,
          width: indicatorPosition.width,
        }}
      />
    </div>
  );
};

